import "@mdxeditor/editor/style.css";

import {
  BoldItalicUnderlineToggles,
  headingsPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import { Box, FormControl, FormHelperText, SxProps } from "@mui/material";
import React, { useEffect, useRef } from "react";

const ToolbarContents = () => (
  <>
    <UndoRedo />
    <BoldItalicUnderlineToggles />
    <ListsToggle />
  </>
);

/**
 * A controlled rich text field component without react-hook-form dependencies.
 * Uses MDXEditor under the hood.
 */
export interface ControlledRichTextFieldProps {
  /** The current markdown value. */
  value: string;
  /** Callback invoked when the markdown changes. */
  onChange: (value: string) => void;
  /** Optional blur handler (captured on the surrounding container). */
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  /** Whether the editor is read-only. */
  disabled?: boolean;
  /** If true, the editor will be styled as having an error. */
  error?: boolean;
  /** Helper text to show beneath the editor (e.g. validation message). */
  helperText?: string;
  /** Optional sx overrides for the editor container. */
  sx?: SxProps;
}

export function ControlledRichTextField({
  value,
  onChange,
  onBlur,
  disabled = false,
  error = false,
  helperText,
}: Readonly<ControlledRichTextFieldProps>) {
  const mdxEditorRef = useRef<MDXEditorMethods>(null);
  const lastValueRef = useRef<string | null>(null);

  useEffect(() => {
    const nextValue = value ?? "";
    if (lastValueRef.current === nextValue) {
      return;
    }

    lastValueRef.current = nextValue;
    mdxEditorRef.current?.setMarkdown(nextValue);
  }, [value]);

  return (
    <FormControl sx={{ height: "100%" }} fullWidth>
      <Box
        onBlurCapture={onBlur}
        sx={[
          (theme) => ({
            padding: "5px",
            border: `1px solid ${error ? theme.palette.error.main : "#0000003b"}`,
            borderRadius: "5px",
            backgroundColor: "#fff",
            '& [class*="toolbarRoot"]': {
              position: "static !important",
              top: "auto !important",
              backgroundColor: "white",
              zIndex: "auto",
            },
          }),
        ]}
      >
        <MDXEditor
          ref={mdxEditorRef}
          readOnly={disabled}
          markdown={value ?? ""}
          onChange={(markdown) => {
            lastValueRef.current = markdown;
            onChange(markdown);
          }}
          plugins={[
            toolbarPlugin({
              toolbarContents: ToolbarContents,
            }),
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
          ]}
        />
        {helperText && (
          <FormHelperText
            sx={(theme) => ({
              color: error ? theme.palette.error.main : undefined,
            })}
          >
            {helperText}
          </FormHelperText>
        )}
      </Box>
    </FormControl>
  );
}
